/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792713600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Oct 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-adh-bigquery",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "BigQuery credential setup",
          "depth": 0,
          "id": "bigquery-credential-setup"
        }, {
          "value": "Create a project and enable BigQuery API",
          "depth": 1,
          "id": "create-a-project-and-enable-bigquery-api"
        }, {
          "value": "Create a service account",
          "depth": 1,
          "id": "create-a-service-account"
        }, {
          "value": "Grant access",
          "depth": 1,
          "id": "grant-access"
        }, {
          "value": "Connect to Google ADH via BigQuery",
          "depth": 0,
          "id": "connect-to-google-adh-via-bigquery"
        }, {
          "value": "Configure your service account",
          "depth": 1,
          "id": "configure-your-service-account"
        }, {
          "value": "Configure your destination",
          "depth": 1,
          "id": "configure-your-destination"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync mode",
          "depth": 1,
          "id": "sync-mode"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ol: "ol",
      li: "li",
      h3: "h3",
      a: "a",
      ul: "ul",
      strong: "strong",
      code: "code",
      img: "img",
      h4: "h4",
      pre: "pre"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you send data from your warehouse into Google BigQuery to join it with Google's Ads Data Hub."
      }), "\n", _jsx(_components.p, {
        children: "Connecting Hightouch to BigQuery requires some setup in both platforms. It's recommended to set up a service account with the correct permissions in BigQuery before configuring the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Big Query table"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "bigquery-credential-setup",
        children: "BigQuery credential setup"
      }), "\n", _jsx(_components.p, {
        children: "Setup in BigQuery has three main steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable BigQuery for your Google Cloud project"
        }), "\n", _jsx(_components.li, {
          children: "Create a service account"
        }), "\n", _jsx(_components.li, {
          children: "Grant the Hightouch service account access to your project"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-project-and-enable-bigquery-api",
        children: "Create a project and enable BigQuery API"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Login to the ", _jsx(_components.a, {
            href: "https://console.developers.google.com/",
            children: "Google Developers Console"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure the ", _jsx(_components.a, {
            href: "https://console.cloud.google.com/",
            children: "Cloud Platform"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["If you don't have a project already, ", _jsx(_components.a, {
                href: "https://support.google.com/cloud/answer/6251787?hl=en&ref_topic=6158848",
                children: "create one"
              }), "."]
            }), "\n", _jsxs(_components.li, {
              children: ["Once you have a project, ", _jsx(_components.a, {
                href: "https://console.cloud.google.com/flows/enableapi?apiid=bigquery&_ga=2.71379221.724057513.1673650275-1611021579.1664923822&_gac=1.213641504.1673650813.EAIaIQobChMIt9GagtPF_AIVkgB9Ch331QRREAAYASAAEgJfrfD_BwE",
                children: "enable the BigQuery API"
              }), " for it."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy your ", _jsx(_components.strong, {
            children: "Project ID"
          }), " for later use."]
        }), "\n", _jsxs(_components.li, {
          children: ["Find the ", _jsx(_components.strong, {
            children: "location"
          }), " of your BigQuery dataset or sets. You can find this by querying the ", _jsx(_components.code, {
            children: "INFORMATION_SCHEMA.SCHEMATA"
          }), " view or by visiting the ", _jsx(_components.a, {
            href: "https://console.cloud.google.com/",
            children: "Google Cloud web console"
          }), " and clicking on a BigQuery dataset in the Explorer panel. You need both ", _jsx(_components.strong, {
            children: "Project ID"
          }), " and ", _jsx(_components.strong, {
            children: "Data location"
          }), " when connecting Hightouch to BigQuery."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-bigquery-data-location.png",
          alt: "Dataset Location in the Google Cloud Console"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure ", _jsx(_components.a, {
            href: "https://support.google.com/cloud/answer/6293499#enable-billing",
            children: "billing is\nenabled"
          }), " on\nyour project, otherwise Hightouch can't write into the cluster."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-service-account",
        children: "Create a service account"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a service account, follow the setup instructions in our ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "Google Cloud Platform (GCP) documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "grant-access",
        children: "Grant access"
      }), "\n", _jsxs(_components.p, {
        children: ["Your GCP service account will need ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/access-control#permissions-predefined-roles",
          children: "permission"
        }), " to read and write data to BigQuery.\nYou can set up your service account to have ", _jsx(_components.a, {
          href: "#grant-full-access",
          children: "full access"
        }), " to your project using a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/understanding-roles#predefined_roles",
          children: "predefined role"
        }), ".\nOtherwise, you can create a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/roles-overview#custom",
          children: "custom role"
        }), " and provide ", _jsx(_components.a, {
          href: "#grant-limited-access",
          children: "limited access"
        }), " according to a user-specified list of permissions."]
      }), "\n", _jsx(_components.h4, {
        id: "grant-full-access",
        children: "Grant full access"
      }), "\n", _jsxs(_components.p, {
        children: ["You can grant full access by assigning the ", _jsx(_components.code, {
          children: "bigquery.user"
        }), ", ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), ", and ", _jsx(_components.code, {
          children: "bigquery.dataEditor"
        }), " roles to your service account.\nYou can do this in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/granting-changing-revoking-access#grant-single-role",
          children: "Google Cloud web console"
        }), " or by running these snippets in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/shell/docs/run-gcloud-commands",
          children: "Cloud Shell"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Grant permission to ", _jsx(_components.strong, {
          children: "read metadata and list tables"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/bigquery.user\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Grant permission to ", _jsx(_components.strong, {
          children: "read data from tables and views"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/bigquery.dataViewer\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Grant permission to ", _jsx(_components.strong, {
          children: "write data to tables and views"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/bigquery.dataEditor\n"
        })
      }), "\n", _jsx(_components.h4, {
        id: "grant-limited-access",
        children: "Grant limited access"
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't want to grant full access to your BigQuery service account, you can opt to grant limited access instead.\nYou can do this by assigning the ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), " and ", _jsx(_components.code, {
          children: "bigquery.dataEditor"
        }), " roles only to the specific datasets, tables, or views you want to use in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["Since you are assigning these roles only to specific resources, you need to assign the ", _jsx(_components.code, {
          children: "bigquery.user"
        }), " role ", _jsx(_components.strong, {
          children: "and"
        }), " grant the ", _jsx(_components.code, {
          children: "bigquery.tables.get"
        }), " permission at the project level.\nFor this, you can create a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/creating-custom-roles#creating",
          children: "custom role"
        }), " in the Google Cloud web console based on an ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/access-control#bigquery.user",
          children: "existing predefined role"
        }), " (", _jsx(_components.code, {
          children: "bigquery.user"
        }), "), which you can name ", _jsx(_components.code, {
          children: "custom.bigquery.user"
        }), ".\nWhen setting up the custom role, click ", _jsx(_components.strong, {
          children: "Add permissions"
        }), " to add the ", _jsx(_components.code, {
          children: "bigquery.tables.get"
        }), " permission to this custom role."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then assign this role to your service account at the project level.\nYou can do this in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/granting-changing-revoking-access#grant-single-role",
          children: "Google Cloud web console"
        }), " or by running this snippet in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/shell/docs/run-gcloud-commands",
          children: "Cloud Shell"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/custom.bigquery.user\n"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If this ", _jsx(_components.code, {
            children: "custom.bigquery.user"
          }), " role still isn't limited enough, you can try\nassigning the\n", _jsx(_components.a, {
            href: "https://cloud.google.com/bigquery/docs/access-control#bigquery.jobUser",
            children: _jsx(_components.code, {
              children: "bigquery.jobUser"
            })
          }), "\nrole ", _jsx(_components.strong, {
            children: "and"
          }), " granting the ", _jsx(_components.code, {
            children: "bigquery.dataset.get"
          }), ", ", _jsx(_components.code, {
            children: "bigquery.tables.get"
          }), ", and\n", _jsx(_components.code, {
            children: "bigquery.tables.list"
          }), " permissions at the project level."]
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch needs to be able to list the schemas and tables in your BigQuery\nproject when setting up your sync configuration."
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google-adh-via-bigquery",
        children: "Connect to Google ADH via BigQuery"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google ADH (Big Query)"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-service-account",
        children: "Configure your service account"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "GCP credentials"
        }), " you ", _jsx(_components.a, {
          href: "#create-a-service-account",
          children: "previously created"
        }), " or click ", _jsx(_components.strong, {
          children: "Create new"
        }), ". To learn more about these credentials, see the ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "Google Cloud Provider (GCP) documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-destination",
        children: "Configure your destination"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the ", _jsx(_components.strong, {
          children: "Project ID"
        }), " for the project you ", _jsx(_components.a, {
          href: "#create-a-project-and-enable-bigquery-api",
          children: "enabled the BigQuery API"
        }), " for and the ", _jsx(_components.strong, {
          children: "Dataset location"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google ADH (BigQuery) destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google ADH (BigQuery) destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
